import { defineComponent, inject, computed, ref, reactive, useListeners, set } from 'vue';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import { useTableRow } from '../../composables/table-row/index.js';
import UiModal from '../ui-modal/UiModal.vue.js';
import UiRenderNothing from '../ui-render-nothing/UiRenderNothing.vue.js';
import { watchlistSelectorFactory } from '../watchlist-selector/setup/index.js';
import { watchlistPositionListFactory } from '../watchlist-position-list/setup/index.js';
import { watchlistPositionAddFactory } from '../watchlist-position-add/setup/index.js';
import { watchlistPositionDeleteFactory } from '../watchlist-position-delete/setup/index.js';
import { watchlistCreateFactory } from '../watchlist-create/setup/index.js';
import { watchlistDeleteFactory } from '../watchlist-delete/setup/index.js';
import { watchlistEditFactory } from '../watchlist-edit/setup/index.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import UiDropdownMenu from '../ui-dropdown-menu/UiDropdownMenu.vue.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Watchlist",
  props: {
    locale: { type: null, required: true },
    defaultId: { type: null, required: false },
    itemsPerPage: { type: Number, required: false, default: 5 },
    suggesterProps: { type: null, required: false }
  },
  emits: ["store-error", "data-load", "watchlist-change", "table-row-click", "table-row-key-click", "watchlist-add-position", "watchlist-position-remove", "watchlist-create", "watchlist-edit", "watchlist-delete"],
  setup(__props, { emit: emits }) {
    var _a;
    const props = __props;
    const translator = inject("translator");
    const numberFormatter = inject("numberFormatter");
    const dateFormatter = inject("dateFormatter");
    const injectedStoreModule = inject("storeModule");
    const WatchlistSelector = watchlistSelectorFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistSelector : void 0
    });
    const WatchlistPositionList = watchlistPositionListFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistPositionList : void 0
    });
    const WatchlistPositionAdd = watchlistPositionAddFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistPositionAdd : void 0
    });
    const WatchlistPositionDelete = watchlistPositionDeleteFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistPositionDelete : void 0
    });
    const WatchlistCreate = watchlistCreateFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistCreate : void 0
    });
    const WatchlistDelete = watchlistDeleteFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistDelete : void 0
    });
    const WatchlistEdit = watchlistEditFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistEdit : void 0
    });
    const componentInputs = computed(() => ({
      language: props.locale,
      itemsPerPage: props.itemsPerPage,
      defaultId: props.defaultId,
      suggesterProps: props.suggesterProps
    }));
    const localizationSupport = useLocalizationSupport(props, { translator, numberFormatter, dateFormatter });
    const { translate } = localizationSupport;
    const storeModule = useStoreModule(
      { storeModule: injectedStoreModule },
      componentInputs,
      emits
    );
    const { storeData, isLoadingStoreData } = storeModule;
    const createWatchlistButton = ref();
    const addPositionButton = ref();
    const watchlistActionDropdownMenu = ref(null);
    const refs = reactive({
      positionActionDropdownMenuRefs: {}
    });
    const watchlistId = ref((_a = props.defaultId) != null ? _a : null);
    const positionId = ref(null);
    const positionIndex = ref(null);
    const addPositionModalToggle = ref(false);
    const deletePositionModalToggle = ref(false);
    const createModalToggle = ref(false);
    const editModalToggle = ref(false);
    const deleteModalToggle = ref(false);
    const openAddPositionModal = () => {
      addPositionModalToggle.value = true;
    };
    const closeAddPositionModal = () => {
      var _a2;
      if (addPositionModalToggle.value) {
        (_a2 = addPositionButton.value) == null ? void 0 : _a2.focus();
      }
      addPositionModalToggle.value = false;
    };
    const openDeletePositionModal = (item, index) => {
      positionId.value = item.id;
      positionIndex.value = index;
      deletePositionModalToggle.value = true;
    };
    const getPositionActionDropdownIdentifier = (index) => {
      return `positionActionDropdownMenu-${index}`;
    };
    const populatePositionActionDropdownMenuRef = (index, element) => {
      set(refs.positionActionDropdownMenuRefs, getPositionActionDropdownIdentifier(index), element);
    };
    const closeDeletePositionModal = () => {
      var _a2, _b;
      if (positionIndex.value !== null) {
        (_b = (_a2 = refs.positionActionDropdownMenuRefs[getPositionActionDropdownIdentifier(positionIndex.value)]) == null ? void 0 : _a2.$refs.toggleButton) == null ? void 0 : _b.focus();
      }
      positionId.value = null;
      positionIndex.value = null;
      deletePositionModalToggle.value = false;
    };
    const onWatchlistChange = () => {
      emits("watchlist-change", watchlistId.value);
    };
    const onPositionListTableRowClick = (item) => {
      emits("table-row-click", item);
    };
    const onPositionListTableRowKeyClick = (item) => {
      emits("table-row-key-click", item);
    };
    const onWatchlistPositionAdded = () => {
      emits("watchlist-add-position");
    };
    const onWatchlistPositionDelete = () => {
      closeDeletePositionModal();
      emits("watchlist-position-remove");
    };
    const openCreateModal = () => {
      createModalToggle.value = true;
    };
    const closeCreateModal = () => {
      if (!createModalToggle.value) {
        return;
      }
      if (createWatchlistButton.value) {
        createWatchlistButton.value.focus();
      } else if (watchlistActionDropdownMenu.value) {
        focusOnWatchlistActionDropdownMenu();
      }
      createModalToggle.value = false;
    };
    const onWatchlistCreate = (newWatchlistId) => {
      closeCreateModal();
      if (newWatchlistId) {
        watchlistId.value = newWatchlistId;
      }
      emits("watchlist-create");
    };
    const openEditModal = () => {
      editModalToggle.value = true;
    };
    const closeEditModal = () => {
      if (!editModalToggle.value) {
        return;
      }
      focusOnWatchlistActionDropdownMenu();
      editModalToggle.value = false;
    };
    const onWatchlistEdit = () => {
      closeEditModal();
      emits("watchlist-edit");
    };
    const openDeleteModal = () => {
      deleteModalToggle.value = true;
    };
    const closeDeleteModal = () => {
      if (!deleteModalToggle.value) {
        return;
      }
      focusOnWatchlistActionDropdownMenu();
      deleteModalToggle.value = false;
    };
    const focusOnWatchlistActionDropdownMenu = () => {
      var _a2, _b;
      (_b = (_a2 = watchlistActionDropdownMenu.value) == null ? void 0 : _a2.$refs.toggleButton) == null ? void 0 : _b.focus();
    };
    const onWatchlistDelete = () => {
      closeDeleteModal();
      watchlistId.value = null;
      emits("watchlist-delete");
    };
    const listeners = useListeners();
    const { hasTableRowKeyClickListener, hasTableRowClickListener } = useTableRow(listeners, emits);
    const positionListClickEvents = () => {
      const events = {};
      if (hasTableRowKeyClickListener.value) {
        events["table-row-key-click"] = onPositionListTableRowKeyClick;
      }
      if (hasTableRowClickListener.value) {
        events["table-row-click"] = onPositionListTableRowClick;
      }
      return events;
    };
    return { __sfc: true, props, emits, translator, numberFormatter, dateFormatter, injectedStoreModule, WatchlistSelector, WatchlistPositionList, WatchlistPositionAdd, WatchlistPositionDelete, WatchlistCreate, WatchlistDelete, WatchlistEdit, componentInputs, localizationSupport, translate, storeModule, storeData, isLoadingStoreData, createWatchlistButton, addPositionButton, watchlistActionDropdownMenu, refs, watchlistId, positionId, positionIndex, addPositionModalToggle, deletePositionModalToggle, createModalToggle, editModalToggle, deleteModalToggle, openAddPositionModal, closeAddPositionModal, openDeletePositionModal, getPositionActionDropdownIdentifier, populatePositionActionDropdownMenuRef, closeDeletePositionModal, onWatchlistChange, onPositionListTableRowClick, onPositionListTableRowKeyClick, onWatchlistPositionAdded, onWatchlistPositionDelete, openCreateModal, closeCreateModal, onWatchlistCreate, openEditModal, closeEditModal, onWatchlistEdit, openDeleteModal, closeDeleteModal, focusOnWatchlistActionDropdownMenu, onWatchlistDelete, listeners, hasTableRowKeyClickListener, hasTableRowClickListener, positionListClickEvents, UiModal, UiRenderNothing, HoistComponent, UiDropdownMenu };
  }
});

export { _sfc_main as default };
